export interface FolderDefinition {
  fieldNumber: number;
  label: string;
  options: string[];
}

export interface CreateFolderDefinitionPayload extends FolderDefinition {
  existingFolderValue: string;
}

export type FolderFieldNumber = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
export type FolderFieldKey = `col${FolderFieldNumber}`;

export const FOLDER_FIELD_NUMBERS: readonly FolderFieldNumber[] = [1, 2, 3, 4, 5, 6, 7, 8];

export interface FolderFieldsValue extends Partial<Record<FolderFieldKey, string | null>> {
  id: number;
  commentCount?: number;
}

export type Folder = FolderFieldsValue;

export interface UpdateFolderDefinitionPayload {
  fieldNumber: number;
  label: string;
}

export interface FolderComment {
  id: number;
  comment: string;
  timestamp: Date | string;
  folderId: number;
  folderCode?: string;
  authorId: number;
  authorName: string;
}

export interface FolderComments {
  id: string;
  comments: FolderComment[];
}

export const getFolderFieldKey = (fieldNumber: number): FolderFieldKey => `col${fieldNumber}` as FolderFieldKey;

export const getPrimaryFolderDefinition = <T extends FolderDefinition>(definitions: T[]): T | undefined =>
  [...definitions].sort((a, b) => a.fieldNumber - b.fieldNumber)[0];

export const getFolderFieldValue = (
  folder: FolderFieldsValue | null | undefined,
  definition: FolderDefinition | null | undefined,
): string => {
  if (!folder || !definition) return '';
  return folder[getFolderFieldKey(definition.fieldNumber)] ?? '';
};

export const getFolderDisplayValue = (
  folder: FolderFieldsValue | null | undefined,
  definitions: FolderDefinition[],
): string => getFolderFieldValue(folder, getPrimaryFolderDefinition(definitions));

export const mapFolderValueToDefinitionLabels = (
  folder: FolderFieldsValue,
  definitions: FolderDefinition[],
): Record<string, string | null> =>
  definitions.reduce(
    (acc, definition) => ({
      ...acc,
      [definition.label]: folder[getFolderFieldKey(definition.fieldNumber)] ?? null,
    }),
    {},
  );
